const EXPORT_DPI = 300;
const MM_TO_PX = EXPORT_DPI / 25.4;

export function mmToPx(mm) {
  return mm * MM_TO_PX;
}

export function computeCoverBaseScale(imageWidth, imageHeight, areaWidth, areaHeight) {
  if (!imageWidth || !imageHeight || !areaWidth || !areaHeight) {
    return 1;
  }
  return Math.max(areaWidth / imageWidth, areaHeight / imageHeight);
}

export function computeScaledImageDimensions(naturalW, naturalH, areaW, areaH, userScale = 1) {
  const baseScale = computeCoverBaseScale(naturalW, naturalH, areaW, areaH);
  const effectiveScale = baseScale * userScale;
  return {
    baseScale,
    effectiveScale,
    scaledW: naturalW * effectiveScale,
    scaledH: naturalH * effectiveScale,
  };
}

export function clampTransform(transform, naturalW, naturalH, areaW, areaH) {
  const userScale = transform?.userScale ?? 1;
  const { scaledW, scaledH } = computeScaledImageDimensions(naturalW, naturalH, areaW, areaH, userScale);
  const maxOffsetX = Math.max(0, (scaledW - areaW) / 2);
  const maxOffsetY = Math.max(0, (scaledH - areaH) / 2);
  const offsetX = Math.min(maxOffsetX, Math.max(-maxOffsetX, transform?.offsetX ?? 0));
  const offsetY = Math.min(maxOffsetY, Math.max(-maxOffsetY, transform?.offsetY ?? 0));
  return { offsetX, offsetY, userScale };
}

export function createDefaultTransform() {
  return {
    offsetX: 0,
    offsetY: 0,
    userScale: 1,
  };
}

export function scalePreviewOffsetToExport(transform, size, previewMaxWidth = 360) {
  const ratio = mmToPx(size.width_mm) / previewMaxWidth;
  return {
    offsetX: (transform?.offsetX ?? 0) * ratio,
    offsetY: (transform?.offsetY ?? 0) * ratio,
    userScale: transform?.userScale ?? 1,
  };
}

function needsCrossOrigin(src) {
  if (!src) return false;
  return !src.startsWith('blob:') && !src.startsWith('data:');
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (needsCrossOrigin(src)) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('failed to load image'));
    img.src = src;
  });
}

export async function renderPolaroidBlob(imageSrc, size, transform, options = {}) {
  const { drawBorder = false } = options;
  const img = await loadImageElement(imageSrc);
  const canvas = document.createElement('canvas');
  const cardW = mmToPx(size.width_mm);
  const cardH = mmToPx(size.height_mm);
  canvas.width = Math.round(cardW);
  canvas.height = Math.round(cardH);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas not supported');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const imageLeft = mmToPx(size.image_left_mm);
  const imageTop = mmToPx(size.image_top_mm);
  const imageW = mmToPx(size.image_width_mm);
  const imageH = mmToPx(size.image_height_mm);

  const clamped = clampTransform(transform, img.naturalWidth, img.naturalHeight, imageW, imageH);
  const { effectiveScale } = computeScaledImageDimensions(
    img.naturalWidth,
    img.naturalHeight,
    imageW,
    imageH,
    clamped.userScale,
  );
  const offsetX = clamped.offsetX;
  const offsetY = clamped.offsetY;

  ctx.save();
  ctx.beginPath();
  ctx.rect(imageLeft, imageTop, imageW, imageH);
  ctx.clip();

  const drawW = img.naturalWidth * effectiveScale;
  const drawH = img.naturalHeight * effectiveScale;
  const centerX = imageLeft + imageW / 2 + offsetX;
  const centerY = imageTop + imageH / 2 + offsetY;
  ctx.drawImage(img, centerX - drawW / 2, centerY - drawH / 2, drawW, drawH);
  ctx.restore();

  if (drawBorder) {
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = Math.max(1, mmToPx(0.2));
    ctx.strokeRect(
      ctx.lineWidth / 2,
      ctx.lineWidth / 2,
      canvas.width - ctx.lineWidth,
      canvas.height - ctx.lineWidth,
    );
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('failed to export polaroid'));
      }
    }, 'image/png');
  });
}

export async function renderPolaroidDataUrl(imageSrc, size, transform, options = {}) {
  const blob = await renderPolaroidBlob(imageSrc, size, transform, options);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('failed to read blob'));
    reader.readAsDataURL(blob);
  });
}
